import React, {
  useCallback, useEffect, useMemo, useState, ReactElement, Fragment,
} from 'react';
import Table from '../../../components/table/table.component';
import TableHead from '../../../components/table-head/table-head.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import Widget from '../../../components/widget/widget.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import ActionButton from '../../../components/action-button/action-button.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import FormFieldCheckbox from '../../../components/form-field-checkbox/form-field-checkbox.component';
import FormFieldDate from '../../../components/form-field-date/form-field-date.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import RemoveIcon from '../../../icons/remove-icon';
import FormatService from '../../../services/format-service';
import StructureService from '../../../services/structure-service';
import FormService from '../../../services/form-service';
import DateService from '../../../services/date-service';
import {
  DEAL_COMMON_EDIT, DEAL_MILESTONES_ITEM_CREATE, DEAL_MILESTONES_ITEM_DELETE, DEAL_MILESTONES_ITEM_EDIT,
} from '../../../constants/entity/permissions';

const DealMilestones: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'Milestones';
  const WIDGET_NAME: string = 'widget-deal-milestones';
  const COLUMNS_TOTAL: number = 4;
  const MAX_REMINDER_OFFSET_LENGTH: number = 3;

  const {
    isEditMode,
    isNewDeal,
    dealData,
    dealMilestonesData,
    dealMilestonesNewItems,
    onAddNewItem,
    onDeleteClick,
    onEditModeButtonClick,
  } = props;

  const [dealMilestonesItems, setDealMilestonesItems]: [Record<string, DealMilestonesItem> | null, Function]
    = useState(null);

  const dealCommonEditPermissions: string[] = useMemo((): string[] => [DEAL_COMMON_EDIT], []);
  const dealMilestonesCommonEditPermissions: string[] = useMemo((): string[] => [
    DEAL_COMMON_EDIT, DEAL_MILESTONES_ITEM_EDIT,
  ], []);
  const dealMilestonesItemEditPermissions: string[] = useMemo((): string[] => [DEAL_MILESTONES_ITEM_EDIT], []);
  const dealMilestonesItemCreatePermissions: string[] = useMemo((): string[] => [DEAL_MILESTONES_ITEM_CREATE], []);
  const dealMilestonesItemDeletePermissions: string[] = useMemo((): string[] => [DEAL_MILESTONES_ITEM_DELETE], []);

  const formPrefixCommon: string = 'dealData.dealCommonData';
  const formPrefixMilestones: string = 'dealData.dealMilestonesData';

  const addNewItem: MouseEventHandlerFunction = useCallback((): void => {
    if (onAddNewItem) {
      onAddNewItem({
        id: StructureService.getNewItemId(dealMilestonesNewItems, dealMilestonesData),
        name: '',
        value: DateService.getDateNow(),
      });
    }
  }, [dealMilestonesData, dealMilestonesNewItems]);

  const deleteClick: Function = useCallback((dealMilestonesItemId: number): void => {
    if (onDeleteClick) {
      onDeleteClick(dealMilestonesItemId);
    }

    if (dealMilestonesItems) {
      const newDealMilestonesItems: Record<string, DealMilestonesItem> = dealMilestonesItems;
      delete newDealMilestonesItems[dealMilestonesItemId];
      setDealMilestonesItems({ ...newDealMilestonesItems });
    }
  }, [dealMilestonesItems, dealMilestonesNewItems]);

  useEffect((): void => {
    if (dealMilestonesData) {
      const initialDealMilestonesItems: Record<string, DealMilestonesItem> = {};
      dealMilestonesData.forEach((dealMilestonesItem: DealMilestonesItem): void => {
        initialDealMilestonesItems[dealMilestonesItem.id] = { ...dealMilestonesItem };
      });
      setDealMilestonesItems(initialDealMilestonesItems);
    }
  }, [dealMilestonesData, isEditMode]);

  return (
    <CheckAccess permissions={dealMilestonesCommonEditPermissions} isEnabled={isEditMode || isNewDeal}>
      <Widget
        isFullWidth
        isFullHeight
        isEditMode={isEditMode}
        withEditMode={!isNewDeal}
        editPermissions={dealMilestonesItemEditPermissions}
        widgetName={WIDGET_NAME}
        defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
        onEditModeButtonClick={onEditModeButtonClick}
      >
        <Table>
          <CheckAccess permissions={dealCommonEditPermissions} isEnabled={isEditMode || isNewDeal}>
            <TableHead isSubHeader>
              <TableRow>
                <TableCell>
                  Name
                </TableCell>
                <TableCell>
                  Date
                </TableCell>
                <TableCell>
                  With Reminder / Days
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody isPlain withZebraRows withHeaderColumn>
              <TableRow>
                <TableCell>
                  Date Received
                </TableCell>
                <TableCell>
                  {
                    (isEditMode || isNewDeal) ? (
                      <FormFieldDate name={`${formPrefixCommon}.receivedDate`} />
                    ) : (dealData && dealData.receivedDate)
                  }
                </TableCell>
                <TableCell />
                <TableCell />
              </TableRow>
              {
                ((isEditMode || isNewDeal) || (dealData && dealData.closedDate)) && (
                  <TableRow>
                    <TableCell>
                      Date Closed
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldDate name={`${formPrefixCommon}.closedDate`} />
                        ) : (dealData && dealData.closedDate)
                      }
                    </TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                )
              }
              {
                (isEditMode || isNewDeal || (dealData && dealData.bidDate)) && (
                  <TableRow>
                    <TableCell>
                      Bid Date
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldDate
                            withBlankValues
                            name={`${formPrefixCommon}.bidDate`}
                          />
                        ) : (dealData && dealData.bidDate)
                      }
                    </TableCell>
                    <TableCell>
                      <FlexContainer>
                        {
                          (isEditMode || isNewDeal) ? (
                            <FormFieldCheckbox
                              withNoOffset
                              withNoLabelOffset
                              name={`${formPrefixCommon}.bidDateWithReminder`}
                            />
                          ) : (
                            dealData && FormatService.formatBooleanValue(dealData.bidDateWithReminder)
                          )
                        }
                        {
                          ((isEditMode || isNewDeal) || (dealData && dealData.bidDateReminderOffset)) && (
                            <Fragment>
                              {' '}
                              /
                              {' '}
                              {
                                (isEditMode || isNewDeal) ? (
                                  <FormFieldNumber
                                    isExtraShort
                                    withNoCommas
                                    withUnderline
                                    name={`${formPrefixCommon}.bidDateReminderOffset`}
                                    maxLength={MAX_REMINDER_OFFSET_LENGTH}
                                  />
                                ) : (
                                  (dealData && dealData.bidDateReminderOffset && (` (${dealData.bidDateReminderOffset} `
                                    + `${dealData.bidDateReminderOffset > 1 ? 'days' : 'day'})`)) || ''
                                )
                              }
                            </Fragment>
                          )
                        }
                      </FlexContainer>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )
              }
            </TableBody>
          </CheckAccess>
          {
            ((dealMilestonesItems && !!Object.values(dealMilestonesItems).length) || (isEditMode || isNewDeal)) && (
              <CheckAccess permissions={dealMilestonesItemEditPermissions} isEnabled={isEditMode || isNewDeal}>
                <TableBody isPlain withZebraRows withHeaderColumn>
                  {
                    [
                      ...((!!dealMilestonesItems && Object.values(dealMilestonesItems)) || []),
                      ...(((isEditMode || isNewDeal) && dealMilestonesNewItems) || []),
                    ].map((dealMilestonesItem: DealMilestonesItem): ReactElement => {
                      const isNewItem: boolean = dealMilestonesNewItems
                        && !!dealMilestonesNewItems.length && !!dealMilestonesNewItems.find((
                          dealMilestonesNewItem: DealMilestonesItem,
                        ): boolean => (
                          dealMilestonesNewItem.id === dealMilestonesItem.id
                        ));
                      const formKeyDealMilestonesItem: string = FormService.getIndexedFormFieldKey(
                        dealMilestonesItem.id,
                      );

                      return (
                        <TableRow isActive={isNewItem && !isNewDeal} key={dealMilestonesItem.id}>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <Fragment>
                                  <FormFieldText
                                    isHidden
                                    name={`${formPrefixMilestones}[${formKeyDealMilestonesItem}].id`}
                                  />
                                  <FormFieldText
                                    name={`${formPrefixMilestones}[${formKeyDealMilestonesItem}].name`}
                                    placeholder="Name"
                                  />
                                </Fragment>
                              ) : dealMilestonesItem.name
                            }
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <FormFieldDate
                                  withBlankValues
                                  name={`${formPrefixMilestones}[${formKeyDealMilestonesItem}].value`}
                                />
                              ) : dealMilestonesItem.value
                            }
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <FormFieldCheckbox
                                  withNoOffset
                                  withNoLabelOffset
                                  name={`${formPrefixMilestones}[${formKeyDealMilestonesItem}].withReminder`}
                                />
                              ) : FormatService.formatBooleanValue(dealMilestonesItem.withReminder)
                            }
                            {
                              (!!dealMilestonesItem.reminderOffset || (isEditMode || isNewDeal)) && (
                                <Fragment>
                                  {' '}
                                  /
                                  {' '}
                                  {
                                    (isEditMode || isNewDeal) ? (
                                      <FormFieldNumber
                                        isExtraShort
                                        withNoCommas
                                        withUnderline
                                        name={`${formPrefixMilestones}[${formKeyDealMilestonesItem}].reminderOffset`}
                                        maxLength={MAX_REMINDER_OFFSET_LENGTH}
                                      />
                                    ) : (
                                      (dealMilestonesItem.reminderOffset && (` (${dealMilestonesItem.reminderOffset} `
                                        + `${dealMilestonesItem.reminderOffset > 1 ? 'days' : 'day'})`)) || ''
                                    )
                                  }
                                </Fragment>
                              )
                            }
                          </TableCell>
                          <TableCell isTextRight>
                            {
                              (isEditMode || isNewDeal) && (
                                <CheckAccess permissions={dealMilestonesItemDeletePermissions}>
                                  <div>
                                    <Tooltip title="Delete">
                                      <ActionButton
                                        onClick={(): void => {
                                          deleteClick(dealMilestonesItem.id);
                                        }}
                                      >
                                        <RemoveIcon />
                                      </ActionButton>
                                    </Tooltip>
                                  </div>
                                </CheckAccess>
                              )
                            }
                          </TableCell>
                        </TableRow>
                      );
                    })
                  }
                  {
                    (isEditMode || isNewDeal) && (
                      <CheckAccess permissions={dealMilestonesItemCreatePermissions}>
                        <TableRowAddNewItem colSpan={COLUMNS_TOTAL} onClick={addNewItem} />
                      </CheckAccess>
                    )
                  }
                </TableBody>
              </CheckAccess>
            )
          }
        </Table>
      </Widget>
    </CheckAccess>
  );
});

export default DealMilestones;
